import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFetch, useDebounced } from "@/hooks/useFetch";
import { dateTime, titleCase } from "@/lib/format";
import { EmptyState, ErrorState, PageHeader, Pager, TableSkeleton, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";

const Row = ({ a }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = (a.changes && Object.keys(a.changes).length > 0) || (a.meta && Object.keys(a.meta).length > 0);
  return (
    <>
      <TableRow className={`${rowCls} ${hasDetail ? "cursor-pointer" : ""}`} onClick={() => hasDetail && setOpen((o) => !o)} data-testid="audit-row">
        <TableCell className={`${tdCls} w-6 text-muted-foreground`}>{hasDetail ? (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}</TableCell>
        <TableCell className={`${tdCls} text-muted-foreground whitespace-nowrap`}>{dateTime(a.created_at)}</TableCell>
        <TableCell className={`${tdCls} font-medium font-mono text-xs`}>{a.action}</TableCell>
        <TableCell className={tdCls}>{titleCase(a.entity_type)}{a.summary && <span className="block text-xs text-muted-foreground truncate max-w-[280px]">{a.summary}</span>}</TableCell>
        <TableCell className={`${tdCls} text-xs text-muted-foreground`}>{a.actor_email}<span className="block text-[10px]">{a.actor_type}</span></TableCell>
        <TableCell className={`${tdCls} text-xs text-muted-foreground font-mono`}>{a.ip || "—"}</TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-secondary/40" data-testid="audit-row-detail">
          <TableCell colSpan={6} className="py-3">
            <div className="grid lg:grid-cols-2 gap-4 text-xs">
              {a.changes && Object.keys(a.changes).length > 0 && (
                <div><p className="eyebrow mb-2">Changes</p><ul className="space-y-1 font-mono">{Object.entries(a.changes).map(([k, v]) => <li key={k} className="break-all"><span className="text-muted-foreground">{k}:</span> {JSON.stringify(v.from)} → {JSON.stringify(v.to)}</li>)}</ul></div>
              )}
              {a.meta && Object.keys(a.meta).length > 0 && <div><p className="eyebrow mb-2">Metadata</p><pre className="font-mono whitespace-pre-wrap break-all">{JSON.stringify(a.meta, null, 2)}</pre></div>}
              <p className="text-muted-foreground lg:col-span-2">Entity {a.entity_id || "—"} · request {a.request_id || "—"}</p>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default function AdminAudit() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [actor, setActor] = useState(params.get("actor") || "");
  const dq = useDebounced(q, 300);
  const da = useDebounced(actor, 300);
  useEffect(() => { document.title = "Audit log · Admin"; }, []);
  const query = useMemo(() => ({ q: dq, actor: da, entity_type: params.get("entity_type") || "", action: params.get("action") || "", page: Number(params.get("page") || 1), page_size: 50 }), [dq, da, params]);
  const { data, loading, error, reload } = useFetch("/admin/audit", query);
  const setParam = (k, v) => { const n = new URLSearchParams(params); if (!v) n.delete(k); else n.set(k, v); if (k !== "page") n.delete("page"); setParams(n, { replace: true }); };

  return (
    <div data-testid="admin-audit-page">
      <PageHeader eyebrow="Control" title="Audit log" description="Append-only record of every privileged action: who did what, to which entity, from where. There is no API to edit or delete entries." />
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setParam("q", e.target.value); }} placeholder="Search summary, action or entity id…" className="pl-9 bg-card rounded-lg" data-testid="audit-search-input" />
        </div>
        <Input value={actor} onChange={(e) => { setActor(e.target.value); setParam("actor", e.target.value); }} placeholder="Actor email" className="bg-card rounded-lg lg:w-56" data-testid="audit-actor-input" />
        <Select value={params.get("entity_type") || "all"} onValueChange={(v) => setParam("entity_type", v === "all" ? "" : v)}>
          <SelectTrigger className="lg:w-[200px] bg-card rounded-lg" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All entities</SelectItem>{(data?.entity_types || []).map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={params.get("action") || ""} onChange={(e) => setParam("action", e.target.value)} placeholder="Action prefix, e.g. product." className="bg-card rounded-lg lg:w-56 font-mono text-xs" data-testid="audit-action-input" />
      </div>
      <div className="bg-card border rounded-xl overflow-hidden">
        {loading && !data ? <TableSkeleton cols={6} rows={10} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data?.items?.length ? <EmptyState title="No audit entries" description="Nothing matches these filters." /> : (
          <Table data-testid="admin-data-table">
            <TableHeader className="bg-secondary/60"><TableRow><TableHead className={thCls} /><TableHead className={thCls}>When</TableHead><TableHead className={thCls}>Action</TableHead><TableHead className={thCls}>Entity</TableHead><TableHead className={thCls}>Actor</TableHead><TableHead className={thCls}>IP</TableHead></TableRow></TableHeader>
            <TableBody>{data.items.map((a) => <Row key={a.id} a={a} />)}</TableBody>
          </Table>
        )}
      </div>
      {data && <div className="mt-3"><Pager page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} onPage={(p) => setParam("page", String(p))} /></div>}
    </div>
  );
}
